import {
  DocumentData,
  QueryConstraint,
  doc,
  endAt,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAt,
  where
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { COLLECTIONS } from "./collectionNames";
import { adminCollection, dateField, logDevelopmentError, stringArrayField, stringField } from "./firestoreHelpers";
import {
  mapActivityEventRecord,
  mapBookingRecord,
  mapPaymentRecord,
  mapShoutOutRecord,
  mapUserRecord,
  PaymentRecord,
  BookingRecord
} from "./realtimeMappers";
import { ActivityEventRecord, AdminUserRecord, ShoutOutRecord } from "./types";

export type UserDirectoryRow = AdminUserRecord & {
  profileStatus: string;
  guardianMinorStatus: string;
};

export type UserGoalSummary = {
  goals: string[];
  guidedJourneyItems: SimpleRecord[];
  nextMoveRecommendationCount: number | null;
};

export type SimpleRecord = {
  id: string;
  title: string;
  subtitle: string;
  date: Date | null;
};

export type UserDetail = {
  user: UserDirectoryRow | null;
  goals: UserGoalSummary;
  activity: ActivityEventRecord[];
  skills: SimpleRecord[];
  routines: SimpleRecord[];
  shoutOutsReceived: ShoutOutRecord[];
  shoutOutsGiven: ShoutOutRecord[];
  payments: PaymentRecord[];
  bookings: BookingRecord[];
  unavailableSections: string[];
};

function directoryRowFromUser(user: AdminUserRecord): UserDirectoryRow {
  return {
    ...user,
    profileStatus: user.isSuspended ? "Suspended" : user.verificationStatus ?? "Active",
    guardianMinorStatus:
      user.accountType === "minor_dancer"
        ? `Minor / ${user.guardianApprovalStatus ?? "guardian status unknown"}`
        : user.accountType === "parent_guardian"
          ? "Parent/guardian"
          : user.parentGuardianUserId
            ? "Guardian-linked"
            : "Standard"
  };
}

function prefixQuery(fieldName: string, searchTerm: string): QueryConstraint[] {
  return [orderBy(fieldName), startAt(searchTerm), endAt(`${searchTerm}\uf8ff`), limit(20)];
}

function uniqueSearchValues(values: Array<string | null | undefined>): string[] {
  return Array.from(new Set(values.map((value) => value?.trim()).filter((value): value is string => Boolean(value))));
}

function logUserSearchDiagnostic(message: string, metadata?: Record<string, unknown>) {
  console.info("[chor-admin] Users search", message, metadata ?? {});
}

function logUserSearchFailure(label: string, error: unknown): never {
  console.error("[chor-admin] Users search query failed", { label, error });
  logDevelopmentError(error);
  throw error;
}

async function getUsersForQuery(label: string, constraints: QueryConstraint[]): Promise<AdminUserRecord[]> {
  try {
    const snapshot = await getDocs(query(adminCollection(COLLECTIONS.users), ...constraints));
    logUserSearchDiagnostic("query count", { label, count: snapshot.size });
    return snapshot.docs.map((document) => mapUserRecord(document.id, document.data()));
  } catch (error) {
    logUserSearchFailure(label, error);
  }
}

async function getExactUser(label: string, userId: string): Promise<AdminUserRecord[]> {
  const snapshot = await getDoc(doc(db, COLLECTIONS.users, userId));
  logUserSearchDiagnostic("document lookup count", { label, count: snapshot.exists() ? 1 : 0 });
  return snapshot.exists() ? [mapUserRecord(snapshot.id, snapshot.data())] : [];
}

async function getUserForUsername(username: string): Promise<AdminUserRecord[]> {
  const label = `username document ${username}`;
  try {
    const snapshot = await getDoc(doc(db, "usernames", username));
    logUserSearchDiagnostic("document lookup count", { label, count: snapshot.exists() ? 1 : 0 });
    const userId = snapshot.exists() ? stringField(snapshot.data(), "userId") : null;
    return userId ? getExactUser(`username target user ${userId}`, userId) : [];
  } catch (error) {
    logUserSearchFailure(label, error);
  }
}

function directoryRowsFromUsers(users: AdminUserRecord[], source: string): UserDirectoryRow[] {
  const merged = new Map<string, AdminUserRecord>();
  for (const user of users) {
    merged.set(user.id, user);
  }
  logUserSearchDiagnostic("returned user count", { source, count: merged.size, ids: Array.from(merged.keys()) });
  return Array.from(merged.values()).map(directoryRowFromUser);
}

export async function searchUsers(searchTerm: string): Promise<UserDirectoryRow[]> {
  const normalized = searchTerm.trim();
  const lowered = normalized.toLowerCase();
  const queries: Array<() => Promise<AdminUserRecord[]>> = [];

  logUserSearchDiagnostic("submitted", { searchTerm: normalized });

  if (!normalized) {
    queries.push(() => getUsersForQuery("recent users", [orderBy("createdAt", "desc"), limit(25)]));
  } else {
    const username = lowered.startsWith("@") ? lowered.slice(1) : lowered;
    const emailValues = uniqueSearchValues([lowered, normalized]);
    const displayNameVariants = uniqueSearchValues([
      normalized,
      normalized
        .split(/\s+/)
        .filter(Boolean)
        .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1).toLowerCase()}`)
        .join(" ")
    ]);

    if (normalized.includes("@")) {
      for (const email of emailValues) {
        const exactEmailUsers = await getUsersForQuery(`exact email ${email}`, [where("email", "==", email), limit(5)]);
        if (exactEmailUsers.length > 0) {
          return directoryRowsFromUsers(exactEmailUsers, `exact email ${email}`);
        }
      }
    }

    queries.push(() => getExactUser(`document id ${normalized}`, normalized));
    queries.push(() => getUsersForQuery(`userId ${normalized}`, [where("userId", "==", normalized), limit(1)]));
    for (const email of emailValues) {
      queries.push(() => getUsersForQuery(`email ${email}`, [where("email", "==", email), limit(5)]));
    }
    queries.push(() => getUserForUsername(username));
    queries.push(() => getUsersForQuery(`username ${username}`, [where("username", "==", username), limit(5)]));
    for (const displayName of displayNameVariants) {
      queries.push(() => getUsersForQuery(`displayName prefix ${displayName}`, prefixQuery("displayName", displayName)));
    }
    queries.push(() => getUsersForQuery(`username prefix ${username}`, prefixQuery("username", username)));
    queries.push(() => getUsersForQuery(`email prefix ${lowered}`, prefixQuery("email", lowered)));
    queries.push(() => getUsersForQuery(`role ${lowered}`, [where("role", "==", lowered), limit(20)]));
    queries.push(() => getUsersForQuery(`activeRole ${lowered}`, [where("activeRole", "==", lowered), limit(20)]));
    queries.push(() => getUsersForQuery(`roles array ${lowered}`, [where("roles", "array-contains", lowered), limit(20)]));
  }

  const results = await Promise.all(queries.map((runQuery) => runQuery()));
  return directoryRowsFromUsers(results.flat(), normalized ? "secondary queries" : "recent users");
}

async function safeDocs(collectionName: string, constraints: QueryConstraint[]): Promise<DocumentData[]> {
  try {
    const snapshot = await getDocs(query(adminCollection(collectionName), ...constraints, limit(10)));
    return snapshot.docs.map((document) => ({ id: document.id, ...document.data() }));
  } catch (error) {
    logDevelopmentError(error);
    return [];
  }
}

function simpleRecord(data: DocumentData, fallbackTitle: string): SimpleRecord {
  return {
    id: String(data.id ?? "unknown"),
    title:
      stringField(data, "title") ??
      stringField(data, "name") ??
      stringField(data, "eventName") ??
      stringField(data, "skillId") ??
      fallbackTitle,
    subtitle:
      stringField(data, "status") ??
      stringField(data, "eventType") ??
      stringField(data, "kind") ??
      stringField(data, "routineId") ??
      "Read-only record",
    date: dateField(data, "createdAt") ?? dateField(data, "updatedAt") ?? dateField(data, "timestamp")
  };
}

function recommendationCount(data: DocumentData | null): number | null {
  const items = data?.items;
  return Array.isArray(items) ? items.length : null;
}

async function userProfile(userId: string): Promise<UserDirectoryRow | null> {
  const snapshot = await getDoc(doc(db, COLLECTIONS.users, userId));
  return snapshot.exists() ? directoryRowFromUser(mapUserRecord(snapshot.id, snapshot.data())) : null;
}

export async function fetchUserDetail(userId: string): Promise<UserDetail> {
  const unavailableSections: string[] = [];
  const user = await userProfile(userId).catch((error) => {
    logDevelopmentError(error);
    return null;
  });

  const [
    activityDocs,
    analyticsDocs,
    skillDocs,
    routineDocs,
    guidedJourneyDocs,
    receivedDocs,
    givenDocs,
    payerPaymentDocs,
    payeePaymentDocs,
    userBookingDocs,
    coachBookingDocs,
    nextMoveSnapshot
  ] = await Promise.all([
    safeDocs(COLLECTIONS.nextParticipationAnalyticsEvents, [where("userId", "==", userId), orderBy("createdAt", "desc")]),
    safeDocs(COLLECTIONS.analyticsEvents, [where("userId", "==", userId), orderBy("timestamp", "desc")]),
    safeDocs(COLLECTIONS.userSkillSummaries, [where("userId", "==", userId), orderBy("skillScore", "desc")]),
    safeDocs(COLLECTIONS.routineAssignments, [where("userId", "==", userId), orderBy("updatedAt", "desc")]),
    safeDocs(COLLECTIONS.guidedJourneyItems, [where("userId", "==", userId), orderBy("createdAt", "desc")]),
    safeDocs(COLLECTIONS.peerShoutOuts, [where("recipientDancerUserId", "==", userId), orderBy("createdAt", "desc")]),
    safeDocs(COLLECTIONS.peerShoutOuts, [where("senderUserId", "==", userId), orderBy("createdAt", "desc")]),
    safeDocs(COLLECTIONS.marketplaceTransactions, [where("payerUserId", "==", userId), orderBy("updatedAt", "desc")]),
    safeDocs(COLLECTIONS.marketplaceTransactions, [where("payeeUserId", "==", userId), orderBy("updatedAt", "desc")]),
    safeDocs(COLLECTIONS.bookings, [where("userId", "==", userId), orderBy("updatedAt", "desc")]),
    safeDocs(COLLECTIONS.bookings, [where("coachUserId", "==", userId), orderBy("updatedAt", "desc")]),
    getDoc(doc(db, COLLECTIONS.nextMoveRecommendations, userId)).catch((error) => {
      logDevelopmentError(error);
      return null;
    })
  ]);

  if (!nextMoveSnapshot) {
    unavailableSections.push("Next-move recommendations unavailable.");
  }

  const paymentMap = new Map<string, PaymentRecord>();
  [...payerPaymentDocs, ...payeePaymentDocs].forEach((data) => {
    const id = String(data.id);
    paymentMap.set(id, mapPaymentRecord(id, data));
  });

  const bookingMap = new Map<string, BookingRecord>();
  [...userBookingDocs, ...coachBookingDocs].forEach((data) => {
    const id = String(data.id);
    bookingMap.set(id, mapBookingRecord(id, data));
  });

  return {
    user,
    goals: {
      goals: user?.goals ?? [],
      guidedJourneyItems: guidedJourneyDocs.map((data) => simpleRecord(data, "Guided journey item")),
      nextMoveRecommendationCount: nextMoveSnapshot?.exists() ? recommendationCount(nextMoveSnapshot.data()) : null
    },
    activity: [...activityDocs, ...analyticsDocs]
      .map((data) => mapActivityEventRecord(String(data.id), data))
      .sort((left, right) => (right.createdAt?.getTime() ?? 0) - (left.createdAt?.getTime() ?? 0))
      .slice(0, 10),
    skills: skillDocs.map((data) => simpleRecord(data, "Skill summary")),
    routines: routineDocs.map((data) => simpleRecord(data, "Routine assignment")),
    shoutOutsReceived: receivedDocs.map((data) => mapShoutOutRecord(String(data.id), data)),
    shoutOutsGiven: givenDocs.map((data) => mapShoutOutRecord(String(data.id), data)),
    payments: Array.from(paymentMap.values()),
    bookings: Array.from(bookingMap.values()),
    unavailableSections
  };
}
